<template>
  <base-modal
    :ref="modal.key"
    :show="open"
    :title="modalTitle"
    :subtitle="connectedService.name"
    @on-confirm="saveItem"
    @on-cancel="hide()"
    :okText="$t('Save')"
    :closeText="$t('Close')"
    maxWidth="60%"
  >
    <v-tabs centered v-model="selectedTab">
      <v-tab>{{ $t("General") }}</v-tab>
      <v-tab>{{ $t("Settings") }}</v-tab>
      <v-tab-item>
        <v-card flat class="my-0">
          <v-card-text>
            <v-select
              v-model="connectedService.serviceName"
              v-bind:label="$t('Service')"
              :items="serviceList"
              item-text="ServiceName"
              item-value="ServiceName"
              :error-messages="serviceNameErrors"
              @change="updateServiceParams"
              @blur="$v.connectedService.serviceName.$touch()"
            ></v-select>
            <v-text-field
              v-bind:label="$t('Description')"
              v-model="connectedService.description"
              :error-messages="descriptionErrors"
              @input="$v.connectedService.description.$touch()"
              @blur="$v.connectedService.description.$touch()"
            ></v-text-field>
          </v-card-text>
        </v-card>
      </v-tab-item>
      <v-tab-item>
        <v-card flat class="my-0">
          <v-card-text>
            <div v-for="(param, index) in connectedService.params" :key="index">
              <v-text-field
                :label="param.label"
                v-model="param.value"
                :error-messages="requiredFieldErrors(index)"
              ></v-text-field>
            </div>
          </v-card-text>
        </v-card>
      </v-tab-item>
    </v-tabs>
  </base-modal>
</template>
<script>
import { required, requiredIf } from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate';

import ConnectedServiceService from '@/services/ConnectedServiceService';
import ConnectedService from '@/ecm/models/connectedService/ConnectedService';
import Utils from '@/ecm/common/Utils';

export default {
    name: 'ConnectedServiceModal',
    created() {
        ConnectedServiceService.getList().then((data) => {
            this.serviceList = data;
        });
    },
    data() {
        return {
            serviceList: [],
            modal: {
                title: this.$t('ConnectedService'),
                key: 'connectedServiceModal',
            },
            open: false,
            connectedService: new ConnectedService(),
            selectedTab: 0,
            isNew: true,
            apiUrl: this.$store.getters['account/apiUrl'],
        };
    },
    mixins: [validationMixin],
    validations: {
        connectedService: {
            serviceName: { required },
            description: { required },
            params: {
                $each: {
                    value: {
                        required: requiredIf(function (model) {
                            return model.isRequired;
                        }),
                    },
                },
            },
        },
    },
    computed: {
        modalInstance() {
            return this.$refs[this.modal.key];
        },
        modalTitle() {
            return (
                this.modal.title +
                (this.connectedService.description
                    ? ' - ' + this.connectedService.description
                    : '')
            );
        },
        descriptionErrors() {
            const errors = [];
            if (!this.$v.connectedService.description.$dirty) return errors;
            !this.$v.connectedService.description.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
        serviceNameErrors() {
            const errors = [];
            if (!this.$v.connectedService.serviceName.$dirty) return errors;
            !this.$v.connectedService.serviceName.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
    },
    methods: {
        requiredFieldErrors(index) {
            const errors = [];
            if (!this.$v.connectedService.params.$each[index].value.$dirty)
                return errors;
            !this.$v.connectedService.params.$each[index].value.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
        saveItem() {
            this.$v.$touch();
            if (this.$v.$invalid) {
                if (
                    this.$v.connectedService.description.$invalid ||
                    this.$v.connectedService.serviceName.$invalid
                ) {
                    this.selectedTab = 0;
                } else {
                    this.selectedTab = 1;
                }
                return;
            }

            this.modalInstance.progress(true);

            if (this.isNew) {
                this.createConnectedService();
            } else {
                this.updateConnectedService();
            }
        },
        new() {
            this.selectedTab = 0;
            this.connectedService = new ConnectedService();
            this.$v.$reset();
            this.show();
        },
        edit(connectedServiceId) {
            ConnectedServiceService.getById(connectedServiceId).then((dto) => {
                this.selectedTab = 1;
                this.isNew = false;
                var defaultParams = this.serviceList.find(
                    (x) => x.ServiceName == dto.ServiceName
                ).Params;
                defaultParams.forEach((param) => {
                    var dtoParam = dto.Params.find((x) => x.ParamName == param.ParamName);
                    if (dtoParam) {
                        param.ParamValue = dtoParam.ParamValue;
                    }
                });
                dto.Params = defaultParams;
                this.connectedService = new ConnectedService(dto);
                this.$v.$reset();
                this.show();
            });
        },
        updateServiceParams(serviceName) {
            var service = this.serviceList.find((x) => x.ServiceName == serviceName);
            this.connectedService = new ConnectedService(service);
        },
        show() {
            this.open = true;
        },
        hide() {
            this.open = false;
            this.isNew = true;
        },
        createConnectedService() {
            const request = this.createRequest();

            ConnectedServiceService.create(request)
                .then((result) => {
                    if (result == null) return;
                    this.addBexioAuth(result);
                    Utils.toast.success(this.$t('ConnectedService') + ' ' + this.$t('CreatedSuccessfully'));
                    this.hide();
                    this.$emit('on-item-saved');
                }).catch(() => {
                    // error
                }).finally(() => {
                    this.modalInstance.progress(false);
                });
        },
        updateConnectedService() {
            const request = this.createRequest();

            ConnectedServiceService.update(this.connectedService.id, request)
                .then((result) => {
                    if (result == null) return;
                    this.addBexioAuth(result);
                    Utils.toast.success(this.$t('ConnectedService') + ' ' + this.$t('UpdatedSuccessfully'));
                    this.hide();
                    this.$emit('on-item-saved');
                }).catch(() => {
                    //error
                }).finally(() => {
                    this.modalInstance.progress(false);
                });
        },
        addBexioAuth(connectedServiceId) {
            if (this.connectedService.serviceName == 'Bexio') {
                const clientId = this.connectedService.params.find(p => p.name == 'ClientID').value;
                window.open(`https://auth.bexio.com/realms/bexio/protocol/openid-connect/auth?response_type=code&state=${connectedServiceId}&client_id=${clientId}&redirect_uri=${this.apiUrl}/api/connected-service/bexio/set-token&scope=contact_show%20accounting%20project_show%20article_show%20bank_account_show%20file%20kb_invoice_edit%20email%20openid%20profile%20kb_bill_show%20offline_access`, '_blank');
            }
        },
        createRequest() {
            const request = {
                ServiceName: this.connectedService.serviceName,
                Description: this.connectedService.description,
                Params: this.connectedService.params.map(p => {
                    return {
                        ParamName: p.name,
                        ParamValue: p.value
                    };
                })
            };

            return request;
        }
    },
};
</script>
